import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { buildDisplayPrice } from './convert'
import { currencyFromCountryCode, detectCurrencyFromBrowser, isValidDisplayCurrency } from './detect'
import { fetchFxRates } from './rates'
import type { DisplayCurrency, DisplayPriceInfo, FxRates } from './types'
import {
  CURRENCY_MANUAL_KEY,
  CURRENCY_STORAGE_KEY,
  DISPLAY_CURRENCIES,
} from './types'

type CurrencyContextValue = {
  currency: DisplayCurrency
  setCurrency: (next: DisplayCurrency, manual?: boolean) => void
  currencies: readonly DisplayCurrency[]
  fx: FxRates | null
  ratesLoading: boolean
  isManual: boolean
  price: (usd: number) => DisplayPriceInfo
  formatUsdCharge: (usd: number) => string
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null)

function readStoredCurrency(): DisplayCurrency | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(CURRENCY_STORAGE_KEY)
    if (raw && isValidDisplayCurrency(raw)) return raw
  } catch {
    /* ignore */
  }
  return null
}

function readManualFlag(): boolean {
  if (typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem(CURRENCY_MANUAL_KEY) === '1'
  } catch {
    return false
  }
}

async function fetchGeoCountry(): Promise<string | null> {
  try {
    const res = await fetch('/api/geo', { signal: AbortSignal.timeout(5000) })
    if (!res.ok) return null
    const data = (await res.json()) as { country?: string }
    return data.country?.trim() || null
  } catch {
    return null
  }
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<DisplayCurrency>(() => readStoredCurrency() ?? 'USD')
  const [isManual, setIsManual] = useState(() => readManualFlag())
  const [fx, setFx] = useState<FxRates | null>(null)
  const [ratesLoading, setRatesLoading] = useState(true)

  const setCurrency = useCallback((next: DisplayCurrency, manual = true) => {
    setCurrencyState(next)
    if (manual) {
      setIsManual(true)
      try {
        localStorage.setItem(CURRENCY_STORAGE_KEY, next)
        localStorage.setItem(CURRENCY_MANUAL_KEY, '1')
      } catch {
        /* ignore */
      }
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void fetchFxRates().then((r) => {
      if (!cancelled) {
        setFx(r)
        setRatesLoading(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (isManual) return
    let cancelled = false

    void (async () => {
      const country = await fetchGeoCountry()
      if (cancelled) return
      const detected = country
        ? currencyFromCountryCode(country)
        : detectCurrencyFromBrowser()
      setCurrencyState(detected)
      try {
        localStorage.setItem(CURRENCY_STORAGE_KEY, detected)
      } catch {
        /* ignore */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isManual])

  const price = useCallback(
    (usd: number) => buildDisplayPrice(usd, currency, fx),
    [currency, fx],
  )

  const formatUsdCharge = useCallback(
    (usd: number) => `$${(Math.round(usd * 100) / 100).toFixed(2)} USD`,
    [],
  )

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
      currencies: DISPLAY_CURRENCIES,
      fx,
      ratesLoading,
      isManual,
      price,
      formatUsdCharge,
    }),
    [currency, setCurrency, fx, ratesLoading, isManual, price, formatUsdCharge],
  )

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext)
  if (!ctx) {
    throw new Error('useCurrency must be used within CurrencyProvider')
  }
  return ctx
}

/** Safe outside provider (e.g. rare SSR); returns USD-only formatting. */
export function useCurrencyOptional(): CurrencyContextValue | null {
  return useContext(CurrencyContext)
}
